/* eslint-disable no-undef */
/**
 * SelectButtons - Option Selector
 * 
 * Supports two modes:
 * - Default: Theme-based styling for various layouts
 * - Enhanced (layout='full'): Gamified, eye-catching design for FullPageQuestionApplication
 */

import React, {Component} from 'react'
import {withStyles} from '@material-ui/core/styles/index'
import parse from 'html-react-parser'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'

import {Grid, FormLabel, Button, FormHelperText, Typography} from '@material-ui/core'

// Base styles - theme-based, used for all layouts except 'full'
const baseStyles = theme => ({
    formControl: {
        margin: theme.spacing(0)
    },
    buttons: {
        position: 'relative'
    },
    button: {
        width: '100%',
        marginRight: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5)
    },
    radio: {
        minWidth: '110px',
        alignItems: 'start'
    },
    selectButtons: {
        marginTop: theme.selectButtons ? theme.selectButtons.marginTop : null,
        [theme.breakpoints.down(600)]: {
            marginTop: theme.selectButtons ? theme.selectButtons.marginTop / 2 : null
        }
    },
    selectButton: {
        fontFamily: theme.fontFamily,
        marginTop: theme.spacing(1),
        width: '100%',
        color: theme.selectButton ? theme.selectButton.color : null,
        borderRadius: theme.selectButton ? theme.selectButton.borderRadius : null,
        border: theme.selectButton ? theme.selectButton.border : null,
        boxShadow: theme.selectButton ? theme.selectButton.boxShadow : null,
        background: theme.selectButton ? theme.selectButton.background : null,
        '&:hover': {
            background: theme.selectButton ? theme.selectButton.hoverBackground : null,
            border: theme.selectButton ? theme.selectButton.hoverBorder : null,
            color: theme.selectButton ? theme.selectButton.hoverColor : null
        }
    },
    selectButtonRoot: {
        textTransform: theme.selectButton ? theme.selectButton.textTransform : null,
        color: theme.selectButton ? theme.selectButton.color : null,
        padding: theme.selectButton ? theme.selectButton.padding : null,
        fontSize: theme.selectxsFontSize,
        fontWeight: theme.selectxsFontWeight,
        letterSpacing: theme.selectButton ? theme.selectButton.letterSpacing : null,
        textShadow: theme.selectButton ? theme.selectButton.textShadow : null,
        lineHeight: 1.3,
        [theme.breakpoints.up(600)]: {
            fontSize: theme.selectmdFontSize,
            fontWeight: theme.selectmdFontWeight
        }
    },
    selectedButtonRoot: {
        background: theme.palette.primaryColor + '!important',
        border: theme.selectedButton ? theme.selectedButton.border : null
    },
    nonSelectedButtonRoot: {
        background: theme.selectButton ? theme.selectButton.nonSelectedBackground : null
    },
    errorText: {
        marginTop: theme.spacing(0.8),
        color: theme.palette.error.main + '!important',
        fontSize: '0.9rem!important',
        textAlign: theme.errorText && theme.errorText.textAlign ? theme.errorText.textAlign : 'center!important'
    },
    lgTitle: {
        fontSize: '1.5rem',
        marginBottom: theme.spacing(1.5),
        color: theme.palette.text.primary,
        lineHeight: 1.15
    },
    customTitleSize: {
        marginTop: 0
    },
    marginBottom: {
        [theme.breakpoints.up(600)]: {
            marginBottom: theme.spacing(3) + 'px!important'
        }
    },
    optionHelper: {
        fontFamily: theme.fontFamily,
        color: theme.body2Color,
        textAlign: theme.optionHelper ? theme.optionHelper.textAlign : ''
    },
    label: {
        marginBottom: theme.spacing(1.5) + 'px!important'
    },
    group: {
        marginTop: 15
    },
    buttonWithoutImage: {
        padding: 6
    },
    checkedIcon: {
        margin: '0 15px',
        position: 'absolute',
        left: theme.checkedIcon && theme.checkedIcon.left ? theme.checkedIcon.left : '7px',
        color: '#ffffff!important'
    },
    optionIcon: {
        margin: '-1.5px 15px',
        position: 'absolute',
        right: '7px',
        [theme.breakpoints.down(450)]: {
            display: 'none'
        }
    },
    optionGroupTitle: {
        fontSize: '15px!important',
        textAlign: theme.optionGroupTitle ? theme.optionGroupTitle.textAlign : 'center',
        marginBottom: -6,
        marginTop: 26,
        [theme.breakpoints.down(450)]: {
            marginTop: 20
        }
    },
    optionGroupTitleFirst: {
        marginTop: '0px!important'
    },
    legend: {
        padding: '0px 0 0 0!important',
        marginBottom: -12,
        [theme.breakpoints.down(600)]: {
            marginBottom: -8,
            padding: '6px 0 0 0!important'
        }
    },
    helperText: {
        marginTop: theme.spacing(1.5),
        fontSize: '0.875rem',
        color: theme.palette.text.primary,
        lineHeight: 1.43
    },
    [theme.breakpoints.down(600)]: {
        questionWrapper: {
            padding: '0px 4px 4px 4px!important'
        },
        selectButton: {
            marginTop: '4px!important'
        },
        selectButtonRoot: {
            paddingTop: theme.spacing(0.8) + 'px!important',
            paddingBottom: theme.spacing(0.8) + 'px!important'
        }
    },
    selectedButtonLabelWrapper: {
        padding: '0 22px',
        zIndex: 1
    },
    // Placeholders for enhanced-only classes (empty in base mode)
    questionSection: {},
    questionSectionContent: {},
    sectionBadge: { display: 'none' },
    sectionBadgeIcon: {},
    optionItem: {},
    radioCircle: { display: 'none' },
    radioCircleSelected: {},
    radioCircleDot: {},
    radioCircleDotSelected: {},
    checkedIconWrapper: {},
    checkedIconVisible: {},
    starburst: { display: 'none' },
    starburstVisible: {},
    ripple: { display: 'none' },
    description: {}
})

// Enhanced styles - for layout='full' (FullPageQuestionApplication)
const enhancedStyles = theme => ({
    // Question section - Animated gradient border sweep
    questionSection: {
        position: 'relative',
        padding: theme.spacing(0.5, 2.5, 3, 2.5),
        margin: theme.spacing(0, -1),
        borderRadius: 23,
        background: '#ffffff',
        boxShadow: `
            0 8px 40px rgba(32, 90, 64, 0.12),
            0 16px 60px rgba(32, 90, 64, 0.08)
        `,
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '300%',
            height: '400%',
            background: 'conic-gradient(from 0deg, #10b981 0deg, #205a40 60deg, #2d7a58 120deg, #10b981 180deg, #205a40 240deg, #205a40 300deg, #10b981 360deg)',
            transformOrigin: 'center center',
            transform: 'translate(-50%, -50%) rotate(0deg)',
            zIndex: 0,
            opacity: 0.5
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            top: 3,
            left: 3,
            right: 3,
            bottom: 3,
            borderRadius: 21,
            background: '#ffffff',
            zIndex: 0
        },
        [theme.breakpoints.down(600)]: {
            padding: theme.spacing(0.25, 2.25, 2.5, 2.25),
            margin: theme.spacing(0, -0.5),
            borderRadius: 16,
            '&::after': {
                borderRadius: 14
            }
        }
    },
    questionSectionContent: {
        position: 'relative',
        zIndex: 1
    },
    '@keyframes borderSweep': {
        '0%': {
            transform: 'translate(-50%, -50%) rotate(0deg)'
        },
        '100%': {
            transform: 'translate(-50%, -50%) rotate(360deg)'
        }
    },

    // Small helper badge under title
    sectionBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: 'rgba(32, 90, 64, 0.1)',
        color: '#205a40',
        padding: '5px 12px',
        borderRadius: 50,
        fontSize: '0.7rem',
        fontWeight: 600,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        marginTop: 18,
        marginBottom: -6,
    },
    sectionBadgeIcon: {
        fontSize: 11
    },

    // Options container
    selectButtons: {
        marginTop: 8,
        padding: '0 !important',
        borderRadius: '0 !important',
        boxShadow: 'none !important',
        background: 'transparent !important',
        backgroundColor: 'transparent !important',
        margin: '0 !important',
        [theme.breakpoints.up('sm')]: {
            marginLeft: -8,
            marginRight: -8
        }
    },
    optionItem: {
        paddingTop: '0 !important',
        paddingBottom: '0 !important',
        paddingLeft: '0 !important',
        paddingRight: '0 !important',
        [theme.breakpoints.up('sm')]: {
            paddingLeft: '8px !important',
            paddingRight: '8px !important'
        }
    },

    // The enhanced button
    selectButton: {
        fontFamily: "'Plus Jakarta Sans', sans-serif!important",
        marginTop: theme.spacing(2),
        width: '100%',
        color: '#1a2e23!important',
        borderRadius: '12px!important',
        border: 'none!important',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)!important',
        backdropFilter: 'blur(10px)',
        boxShadow: `
            0 4px 8px rgba(32, 90, 64, 0.12),
            0 8px 32px rgba(32, 90, 64, 0.02),
            0 0 0 1px rgba(32, 90, 64, 0.1)
        `,
        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)!important',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
            boxShadow: `0 8px 32px rgba(32, 90, 64, 0.3)`
        },
        '&:active': {
            transform: 'scale(0.98) translateY(0)'
        }
    },
    '@keyframes shimmer': {
        '0%': {left: '-100%'},
        '50%, 100%': {left: '100%'}
    },

    selectButtonRoot: {
        textTransform: 'none!important',
        color: '#1a2e23!important',
        padding: '18px 24px!important',
        fontSize: '1.1rem!important',
        fontWeight: '600!important',
        letterSpacing: '-0.01em!important',
        lineHeight: 1.4,
        textAlign: 'left!important',
        justifyContent: 'flex-start!important',
        [theme.breakpoints.up(600)]: {
            fontSize: '1.15rem!important',
            padding: '20px 28px!important'
        }
    },

    // Selected state
    selectedButtonRoot: {
        background: 'linear-gradient(135deg, rgba(32, 90, 64, 0.12) 0%, rgba(45, 122, 88, 0.18) 100%)!important',
        boxShadow: `
            0 0 0 3px rgba(32, 90, 64, 0.25),
            0 8px 32px rgba(32, 90, 64, 0.2),
            0 0 60px rgba(32, 90, 64, 0.1),
            inset 0 1px 0 rgba(255,255,255,0.9)
        `,
        color: '#205a40!important',
        animation: '$selectedPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)!important',
        '&::before': {
            animation: 'none!important'
        },
        '&:hover': {
            transform: 'none',
            boxShadow: `
                0 0 0 3px rgba(32, 90, 64, 0.25),
                0 8px 32px rgba(32, 90, 64, 0.2),
                0 0 60px rgba(32, 90, 64, 0.1),
                inset 0 1px 0 rgba(255,255,255,0.9)
            `
        }
    },
    '@keyframes selectedPop': {
        '0%': {transform: 'scale(1)'},
        '30%': {transform: 'scale(1.03)'},
        '60%': {transform: 'scale(0.98)'},
        '100%': {transform: 'scale(1)'}
    },

    nonSelectedButtonRoot: {
        background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)!important'
    },

    // Ripple effect
    ripple: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: 10,
        height: 10,
        borderRadius: '50%',
        background: 'rgba(32, 90, 64, 0.3)',
        transform: 'translate(-50%, -50%) scale(0)',
        animation: '$rippleWave 0.6s ease-out forwards',
        pointerEvents: 'none'
    },
    '@keyframes rippleWave': {
        '0%': {
            transform: 'translate(-50%, -50%) scale(0)',
            opacity: 1
        },
        '100%': {
            transform: 'translate(-50%, -50%) scale(40)',
            opacity: 0
        }
    },

    // Error text
    errorText: {
        marginTop: theme.spacing(2),
        color: '#dc2626!important',
        fontSize: '0.9rem!important',
        textAlign: 'center!important',
        fontFamily: "'Plus Jakarta Sans', sans-serif!important",
        fontWeight: 500
    },

    // Question title
    legend: {
        padding: '0!important',
        marginBottom: 12,
        fontFamily: "'Plus Jakarta Sans', sans-serif!important",
        fontSize: '1.35rem!important',
        fontWeight: '700!important',
        color: '#1a2e23!important',
        lineHeight: '1.3!important',
        letterSpacing: '-0.02em',
        textAlign: 'center',
        [theme.breakpoints.down(600)]: {
            marginBottom: 8,
            fontSize: '1.2rem!important'
        }
    },
    customTitleSize: {
        marginTop: 0,
        fontFamily: "'Plus Jakarta Sans', sans-serif!important",
        color: '#1a2e23!important',
        fontWeight: '700!important',
        textAlign: 'center'
    },

    // Radio circle
    radioCircle: {
        width: 28,
        height: 28,
        borderRadius: '50%',
        border: '3px solid rgba(32, 90, 64, 0.25)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 16,
        flexShrink: 0,
        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        background: '#ffffff'
    },
    radioCircleSelected: {
        borderColor: '#205a40',
        background: 'linear-gradient(135deg, #205a40 0%, #2d7a58 100%)',
        boxShadow: '0 3px 12px rgba(32, 90, 64, 0.4), 0 0 20px rgba(32, 90, 64, 0.2)'
    },
    radioCircleDot: {
        width: 10,
        height: 10,
        borderRadius: '50%',
        background: '#ffffff',
        opacity: 0,
        transform: 'scale(0)',
        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
    },
    radioCircleDotSelected: {
        opacity: 1,
        transform: 'scale(1)'
    },

    // Check icon with starburst
    checkedIconWrapper: {
        position: 'absolute',
        right: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    checkedIcon: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff!important',
        opacity: 0,
        transform: 'scale(0) rotate(-180deg)',
        transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
        boxShadow: '0 4px 16px rgba(16, 185, 129, 0.5), 0 0 30px rgba(16, 185, 129, 0.3)',
        zIndex: 2
    },
    checkedIconVisible: {
        display: 'none',
        opacity: 1,
        transform: 'scale(1) rotate(0deg)',
        animation: '$checkBounce 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
    },
    '@keyframes checkBounce': {
        '0%': {transform: 'scale(0) rotate(-180deg)'},
        '50%': {transform: 'scale(1.3) rotate(10deg)'},
        '75%': {transform: 'scale(0.9) rotate(-5deg)'},
        '100%': {transform: 'scale(1) rotate(0deg)'}
    },
    starburst: {
        display: 'none',
        position: 'absolute',
        width: 60,
        height: 60,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(16, 185, 129, 0.4) 0%, transparent 70%)',
        opacity: 0,
        transform: 'scale(0)',
        zIndex: 1
    },
    starburstVisible: {
        animation: '$starburstPop 0.6s ease-out forwards'
    },
    '@keyframes starburstPop': {
        '0%': {opacity: 1, transform: 'scale(0)'},
        '50%': {opacity: 1, transform: 'scale(1.5)'},
        '100%': {opacity: 0, transform: 'scale(2)'}
    },

    // Question wrapper
    questionWrapper: {
        padding: '0 !important',
        width: '100%'
    },

    // Other styles
    optionHelper: {
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        color: '#64748b',
        textAlign: 'center',
        marginTop: 8
    },
    optionIcon: {
        margin: '-1.5px 15px',
        position: 'absolute',
        right: '7px',
        [theme.breakpoints.down(450)]: {
            display: 'none'
        }
    },
    optionGroupTitle: {
        fontSize: '13px!important',
        fontFamily: "'Plus Jakarta Sans', sans-serif!important",
        fontWeight: '600!important',
        color: '#64748b!important',
        textAlign: 'center',
        marginBottom: 8,
        marginTop: 28,
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    optionGroupTitleFirst: {
        marginTop: '12px!important'
    },
    helperText: {
        marginTop: theme.spacing(2.5),
        fontSize: '0.9rem',
        color: '#64748b',
        lineHeight: 1.5,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        textAlign: 'center'
    },
    [theme.breakpoints.down(600)]: {
        questionWrapper: {
            padding: '0px!important'
        },
        selectButton: {
            marginTop: '14px!important'
        },
        selectButtonRoot: {
            paddingTop: '16px!important',
            paddingBottom: '16px!important'
        }
    },
    selectedButtonLabelWrapper: {
        paddingRight: 44,
        zIndex: 1
    },
    description: {
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontSize: '1rem',
        color: '#4a5d52',
        lineHeight: 1.6,
        marginTop: 8,
        marginBottom: 12,
        textAlign: 'center'
    },
    marginBottom: {}
})

// Merge styles based on layout
const styles = theme => {
    const base = baseStyles(theme)
    const enhanced = enhancedStyles(theme)
    
    // Return a function that will be called with the layout prop
    // For now, return base styles - the component will handle the merge
    return base
}

class SelectButtonsQuestion extends Component {
    state = {
        showRipple: false,
        rippleKey: 0
    }

    handleChange = value => {
        // Trigger ripple only in enhanced mode
        if (this.props.layout === 'full') {
            this.setState({showRipple: true, rippleKey: Date.now()})
            setTimeout(() => this.setState({showRipple: false}), 600)
        }

        this.props.onValueChanged(this.props.question.id, value)
    }

    render() {
        const {classes, question, value, className, questionProgress, layout} = this.props
        const {showRipple, rippleKey} = this.state
        const hasValue = value !== null && value !== undefined
        const isEnhanced = layout === 'full'

        // For base mode, render the original simple version
        if (!isEnhanced) {
            return (
                <Grid
                    item
                    xs={parseInt(question.xsCols)}
                    sm={parseInt(question.smCols)}
                    className={classes.questionWrapper + ' ' + className + ' question-type-' + question.type}
                    id={'question-' + question.id}
                >
                    {!question.titleSize && (
                        <FormLabel component="legend" className={'question-title ' + classes.legend} id={'question-' + question.id + '-title'}>
                            {question.title}
                        </FormLabel>
                    )}
                    {question.titleSize && (
                        <Typography
                            variant={question.titleSize}
                            gutterBottom
                            className={'question-title ' + classes.customTitleSize + ' ' + (question.titleSize === 'h4' ? classes.marginBottom : null)}
                            id={'question-' + question.id + '-title'}
                        >
                            {question.title}
                        </Typography>
                    )}
                    {question.description && (
                        <Typography
                            component="span"
                            style={{marginBottom: 4}}
                            className={classes.description}
                            id={'question-' + question.id + '-description'}
                        >
                            {parse(question.description)}
                        </Typography>
                    )}
                    <Grid container spacing={1} className={question.title ? classes.selectButtons : null} id={'question-' + question.id + '-options'}>
                        {question.options &&
                        question.options.map((option, key) => {
                            return [
                                option.group_title && (
                                    <Grid item xs={12} sm={12} key={question.id + '|g|' + key}>
                                        <Typography
                                            component="span"
                                            className={classes.optionGroupTitle + ' ' + (key === 0 ? classes.optionGroupTitleFirst : null)}
                                        >
                                            {option.group_title}
                                        </Typography>
                                    </Grid>
                                ),
                                <Grid
                                    item
                                    xs={parseInt(option.xsCols)}
                                    sm={parseInt(option.smCols)}
                                    key={question.id + '|' + key}
                                    style={{paddingBottom: 0}}
                                >
                                    <Button
                                        variant={'contained'}
                                        color={value === option.value ? 'primary' : 'default'}
                                        classes={{
                                            root:
                                                classes.selectButtonRoot +
                                                ' ' +
                                                (value === option.value ? classes.selectedButtonRoot : classes.nonSelectedButtonRoot)
                                        }}
                                        className={classes.selectButton}
                                        style={value === option.value ? {color: '#ffffff'} : null}
                                        onClick={() => this.handleChange(option.value)}
                                        key={key}
                                    >
                                        {value === option.value && (
                                            <FontAwesomeIcon className={classes.checkedIcon} icon={['fas', 'check']}/>
                                        )}
                                        <span className={value === option.value ? classes.selectedButtonLabelWrapper : null}>
                                            {option.label}
                                        </span>
                                        {option.icon && <FontAwesomeIcon className={classes.optionIcon} icon={['far', option.icon]}/>}
                                    </Button>
                                    {option.description && <FormHelperText className={classes.optionHelper}>{option.description}</FormHelperText>}
                                </Grid>
                            ]
                        })}
                        {question.valid === false && (
                            <Grid item xs={12} style={{paddingTop: 0, paddingBottom: 0}}>
                                <FormHelperText className={classes.errorText}>{question.errorMessage}</FormHelperText>
                            </Grid>
                        )}
                        {question.helperText && (
                            <Grid item xs={12}>
                                <FormHelperText className={classes.helperText}>{parse(question.helperText)}</FormHelperText>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            )
        }

        // Enhanced mode for layout='full'
        return (
            <>
                <Grid
                    item
                    xs={parseInt(question.xsCols)}
                    sm={parseInt(question.smCols)}
                    className={classes.questionWrapper + ' ' + className + ' question-type-' + question.type}
                    id={'question-' + question.id}
                >
                    <div className={classes.questionSection}>
                        <div className={classes.questionSectionContent}>
                            {/* Helper badge - shows question progress when provided (e.g., on prelander) */}
                            {!hasValue && questionProgress && (
                                <div style={{display: 'flex', justifyContent: 'center'}}>
                                    <span className={classes.sectionBadge}>
                                        <FontAwesomeIcon icon={['far', 'hand-pointer']} className={classes.sectionBadgeIcon}/>
                                        Vastaa kysymykseen {questionProgress.current} / {questionProgress.total}
                                    </span>
                                </div>
                            )}

                            {/* Question title */}
                            {!question.titleSize && (
                                <FormLabel component="legend" className={'question-title ' + classes.legend}
                                           id={'question-' + question.id + '-title'}>
                                    {question.title}
                                </FormLabel>
                            )}

                            {question.titleSize && (
                                <Typography
                                    variant={question.titleSize}
                                    gutterBottom
                                    className={'question-title ' + classes.customTitleSize + ' ' + (question.titleSize === 'h4' ? classes.marginBottom : null)}
                                    id={'question-' + question.id + '-title'}
                                >
                                    {question.title}
                                </Typography>
                            )}
                            {/* Description */}
                            {question.description && (
                                <Typography
                                    component="span"
                                    className={classes.description}
                                    id={'question-' + question.id + '-description'}
                                >
                                    {parse(question.description)}
                                </Typography>
                            )}

                            {/* Options */}
                            <Grid container spacing={1} className={question.title ? classes.selectButtons : null}
                                  id={'question-' + question.id + '-options'}>
                                {question.options &&
                                    question.options.map((option, key) => {
                                        const isSelected = value === option.value

                                        return [
                                            option.group_title && (
                                                <Grid item xs={12} sm={12} key={question.id + '|g|' + key}>
                                                    <Typography
                                                        component="span"
                                                        className={classes.optionGroupTitle + ' ' + (key === 0 ? classes.optionGroupTitleFirst : null)}
                                                    >
                                                        {option.group_title}
                                                    </Typography>
                                                </Grid>
                                            ),
                                            <Grid
                                                item
                                                xs={parseInt(option.xsCols)}
                                                sm={parseInt(option.smCols)}
                                                key={question.id + '|' + key}
                                                className={classes.optionItem}
                                            >
                                                <Button
                                                    variant="contained"
                                                    disableElevation
                                                    classes={{
                                                        root: classes.selectButtonRoot + ' ' +
                                                            (isSelected ? classes.selectedButtonRoot : classes.nonSelectedButtonRoot)
                                                    }}
                                                    className={classes.selectButton}
                                                    onClick={() => this.handleChange(option.value)}
                                                >
                                                    {/* Ripple effect */}
                                                    {showRipple && isSelected && (
                                                        <span key={rippleKey} className={classes.ripple}/>
                                                    )}

                                                    {/* Radio circle indicator */}
                                                    <span className={
                                                        classes.radioCircle + ' ' +
                                                        (isSelected ? classes.radioCircleSelected : '')
                                                    }>
                                                    <span className={
                                                        classes.radioCircleDot + ' ' +
                                                        (isSelected ? classes.radioCircleDotSelected : '')
                                                    }/>
                                                </span>

                                                    {/* Label text */}
                                                    <span className={'selected-button-label-wrapper ' + (isSelected ? classes.selectedButtonLabelWrapper : null)}>
                                                    {option.label}
                                                </span>

                                                    {/* Check icon with starburst */}
                                                    <span className={classes.checkedIconWrapper}>
                                                    <span className={
                                                        classes.starburst + ' ' +
                                                        (isSelected ? classes.starburstVisible : '')
                                                    }/>
                                                    <span className={
                                                        classes.checkedIcon + ' ' +
                                                        (isSelected ? classes.checkedIconVisible : '')
                                                    }>
                                                        <FontAwesomeIcon icon={['fas', 'check']} style={{fontSize: 14}}/>
                                                    </span>
                                                </span>

                                                    {option.icon && <FontAwesomeIcon className={classes.optionIcon} icon={['far', option.icon]}/>}
                                                </Button>
                                                {option.description &&
                                                    <FormHelperText className={classes.optionHelper}>{option.description}</FormHelperText>}
                                            </Grid>
                                        ]
                                    })}
                                {question.valid === false && (
                                    <Grid item xs={12} style={{paddingTop: 8, paddingBottom: 0}}>
                                        <FormHelperText className={classes.errorText}>{question.errorMessage}</FormHelperText>
                                    </Grid>
                                )}
                                {question.helperText && (
                                    <Grid item xs={12}>
                                        <FormHelperText className={classes.helperText}>{parse(question.helperText)}</FormHelperText>
                                    </Grid>
                                )}
                            </Grid>
                        </div>
                    </div>
                </Grid>
            </>
        )
    }
}

// Subtle styles - for signup pages in layout='full' (professional form appearance)
const subtleStyles = theme => ({
    formControl: {
        margin: theme.spacing(0)
    },
    buttons: {
        position: 'relative'
    },
    button: {
        width: '100%',
        marginRight: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5)
    },
    radio: {
        minWidth: '110px',
        alignItems: 'start'
    },
    selectButtons: {
        marginTop: 8,
    },
    selectButton: {
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif!important",
        marginTop: 4,
        width: '100%',
        borderRadius: '12px!important',
        border: '1.5px solid rgba(32, 90, 64, 0.2)!important',
        boxShadow: 'none!important',
        background: '#ffffff!important',
        transition: 'all 0.2s ease!important',
        '&:hover': {
            background: 'rgba(32, 90, 64, 0.04)!important',
            borderColor: 'rgba(32, 90, 64, 0.4)!important',
        }
    },
    selectButtonRoot: {
        textTransform: 'none!important',
        color: '#1a2e23!important',
        padding: '10px 16px 10px 44px!important', // Extra left padding for checkmark
        fontSize: '0.95rem!important',
        fontWeight: '500!important',
        letterSpacing: '0!important',
        lineHeight: 1.4,
        justifyContent: 'flex-start!important',
        position: 'relative',
    },
    selectedButtonRoot: {
        background: 'linear-gradient(135deg, rgba(32, 90, 64, 0.08) 0%, rgba(32, 90, 64, 0.12) 100%)!important',
        border: '2px solid #205a40!important',
        color: '#1a2e23!important',
        paddingLeft: '44px!important', // Maintain left padding when selected
    },
    nonSelectedButtonRoot: {
        background: '#ffffff!important'
    },
    errorText: {
        marginTop: theme.spacing(0.8),
        color: '#dc2626!important',
        fontSize: '0.875rem!important',
        textAlign: 'left!important',
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    lgTitle: {
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif!important",
        fontSize: '1rem!important',
        fontWeight: '600!important',
        color: '#1a2e23!important',
        marginBottom: 4,
        lineHeight: 1.3
    },
    customTitleSize: {
        marginTop: 0
    },
    marginBottom: {},
    optionHelper: {
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        color: '#6a7d72',
        textAlign: 'left',
        marginTop: 4,
        fontSize: '0.8rem'
    },
    label: {
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif!important",
        fontSize: '1rem!important',
        fontWeight: '600!important',
        color: '#1a2e23!important',
        marginBottom: '4px!important'
    },
    group: {
        marginTop: 8
    },
    buttonWithoutImage: {
        padding: 6
    },
    checkedIcon: {
        width: 22,
        height: 22,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #205a40 0%, #2d7a58 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff!important',
        opacity: 0,
        transform: 'scale(0)',
        transition: 'all 0.2s ease',
        zIndex: 10,
        boxShadow: '0 2px 8px rgba(32, 90, 64, 0.3)',
    },
    checkedIconVisible: {
        opacity: 1,
        transform: 'scale(1)',
    },
    optionIcon: {
        margin: '-1.5px 15px',
        position: 'absolute',
        right: '7px',
        [theme.breakpoints.down(450)]: {
            display: 'none'
        }
    },
    optionGroupTitle: {
        fontSize: '13px!important',
        fontFamily: "'Plus Jakarta Sans', sans-serif!important",
        fontWeight: '600!important',
        color: '#6a7d72!important',
        textAlign: 'left',
        marginBottom: 4,
        marginTop: 16,
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    optionGroupTitleFirst: {
        marginTop: '0px!important'
    },
    legend: {
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif!important",
        fontSize: '1rem!important',
        fontWeight: '600!important',
        color: '#1a2e23!important',
        padding: '0px 0 0 0!important',
        marginBottom: 4,
    },
    helperText: {
        marginTop: theme.spacing(1),
        fontSize: '0.8rem',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        color: '#6a7d72',
        lineHeight: 1.4
    },
    questionWrapper: {
        // Inherit normal MuiGrid padding to match other form elements
    },
    [theme.breakpoints.down(600)]: {
        questionWrapper: {
            // Inherit normal MuiGrid padding on mobile too
        },
        selectButton: {
            marginTop: '4px!important'
        },
        selectButtonRoot: {
            padding: '8px 14px 8px 40px!important', // Maintain left padding for checkmark on mobile
        }
    },
    selectedButtonLabelWrapper: {
        zIndex: 1
    },
    // Hide complex elements in subtle mode
    questionSection: {},
    questionSectionContent: {},
    sectionBadge: { display: 'none' },
    sectionBadgeIcon: {},
    optionItem: {},
    radioCircle: { display: 'none' },
    radioCircleSelected: {},
    radioCircleDot: {},
    radioCircleDotSelected: {},
    checkedIconWrapper: {
        position: 'absolute',
        left: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10,
    },
    starburst: { display: 'none' },
    starburstVisible: {},
    ripple: { display: 'none' },
    description: {
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        color: '#6a7d72',
        fontSize: '0.875rem',
        lineHeight: 1.5,
        marginBottom: 8
    }
})

// Pre-create styled components at module level to prevent re-renders
const SelectButtonsWithBaseStyles = withStyles(styles)(SelectButtonsQuestion)
const SelectButtonsWithEnhancedStyles = withStyles(enhancedStyles)(SelectButtonsQuestion)
const SelectButtonsWithSubtleStyles = withStyles(subtleStyles)(SelectButtonsQuestion)

// Style switcher - returns pre-created components to prevent re-mounting
class SelectButtonsStyleSwitcher extends Component {
    render() {
        const {layout, isSignupPage, ...props} = this.props
        
        if (layout === 'full') {
            if (isSignupPage) {
                // Use subtle styles for signup pages in full layout
                return <SelectButtonsWithSubtleStyles {...props} layout={layout} isSignupPage={isSignupPage} />
            }
            // Use enhanced styles for regular questions in full layout
            return <SelectButtonsWithEnhancedStyles {...props} layout={layout} isSignupPage={isSignupPage} />
        }
        
        // Use base styles for other layouts
        return <SelectButtonsWithBaseStyles {...props} layout={layout} isSignupPage={isSignupPage} />
    }
}

export default SelectButtonsStyleSwitcher
